/* eslint-disable no-console */
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { debounce } from 'lodash';
import domReady from '../helpers/dom-ready';
import DashedBar from '../features/dashed-bar';

class Menu {
  static init() {
    return new Menu();
  }

  constructor() {
    this.menu = document.querySelector('.brix-menu');
    this.logo = document.querySelector('.logo');
    this.toggleMenuButton = document.querySelector('.menu-button');
    this.menuWrapper = document.querySelector('.menu-wrapper');
    this.menuList = document.querySelector('.menu');
    this.menuListItems = document.querySelectorAll('.menu .menu-item');
    this.menuDashedBarContainer = document.querySelector('.menu-dashed-bar');
    this.menuBackground = document.querySelector('.transparent-background');
    this.pageBackground = document.querySelectorAll('section:not(.-inner)');
    this.burgerWhite = document.querySelector('.white');
    this.burgerBlack = document.querySelector('.black');
    this.scrollWrapper = document.querySelector('.parallax-wrapper');

    this.isMenuOpen = false;
    this.overflowBreakpoint = null;

    this.calculateLineColors();
    this.checkBackgroundColor();
    this.addEventListeners();
    this.setupLanguageSwitcher();
  }

  addEventListeners() {
    window.addEventListener('resize', debounce(() => {
      this.checkOpenMenu();
    }), 300);

    window.addEventListener('scroll', debounce(() => {
      this.checkBackgroundColor();
    }), 500);

    this.toggleMenuButton.addEventListener('click', () => {
      this.toggleIsMenuOpen();
    });

    this.menuBackground.addEventListener('click', () => {
      this.toggleIsMenuOpen();
    });

    this.menu.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'opacity') {
        this.menu.classList.remove('-closing');
      }
    });
  }

  pickHex(color1, color2, weight) {
    const w1 = weight;
    const w2 = 1 - w1;
    const rgb = [Math.round(color1[0] * w1 + color2[0] * w2),
      Math.round(color1[1] * w1 + color2[1] * w2),
      Math.round(color1[2] * w1 + color2[2] * w2)];
    return rgb;
  }

  calculateLineColors() {
    if (!document.body.classList.contains('powerfoyle') || !this.menuList || this.menuListItems.length < 1) return;

    const gradient = [
      [255, 255, 55],
      [253, 184, 57],
      [250, 68, 60],
      [250, 73, 179],
    ];

    const listHeight = this.menuList.offsetHeight;
    const sectionHeight = listHeight / 3;

    this.menuListItems.forEach((item) => {
      const element = item;
      const linePosition = item.offsetTop + item.offsetHeight;
      if (linePosition < sectionHeight) {
        const weight = Number((linePosition / sectionHeight).toFixed(2));
        const color = this.pickHex(gradient[0], gradient[1], 1 - weight);
        element.style.borderColor = `rgb(${color.join(',')})`;
      } else if (linePosition < sectionHeight * 2) {
        const weight = Number(((linePosition - sectionHeight) / sectionHeight).toFixed(2));
        const color = this.pickHex(gradient[1], gradient[2], 1 - weight);
        element.style.borderColor = `rgb(${color.join(',')})`;
      } else {
        const weight = Number(((linePosition - (sectionHeight * 2)) / sectionHeight).toFixed(2));
        const color = this.pickHex(gradient[2], gradient[3], 1 - weight);
        element.style.borderColor = `rgb(${color.join(',')})`;
      }
    });
  }

  checkOpenMenu() {
    const breakpoint = this.overflowBreakpoint ? this.overflowBreakpoint : 576;

    if (window.innerWidth > breakpoint && this.isMenuOpen) {
      this.toggleIsMenuOpen();
    }
  }

  toggleIsMenuOpen() {
    this.menu.classList.toggle('-open-menu');
    this.toggleMenuButton.classList.toggle('-open-menu');
    this.isMenuOpen = !this.isMenuOpen;
    const dashedBar = new DashedBar();

    const languageSwitcher = this.menu.querySelector('.language-switcher');

    if (this.isMenuOpen) {
      dashedBar.calcLines(this.menuDashedBarContainer);

      this.menuBackground.setAttribute('style', 'cursor: pointer');
      disableBodyScroll(this.menu);

      if (languageSwitcher) {
        languageSwitcher.classList.add('-light');
      }
    } else {
      enableBodyScroll(this.menu);

      this.menu.classList.add('-closing');

      if (languageSwitcher) {
        languageSwitcher.classList.remove('-light');
      }
    }
  }

  getBackgroundColor(backgroundColor) {
    const R = parseInt(backgroundColor.substring(4, backgroundColor.length - 1).split(',')[0], 10);
    const G = parseInt(backgroundColor.substring(4, backgroundColor.length - 1).split(',')[1], 10);
    const B = parseInt(backgroundColor.substring(4, backgroundColor.length - 1).split(',')[2], 10);
    const A = parseInt(backgroundColor.substring(4, backgroundColor.length - 1).split(',')[3] || 1, 10);
    const brightness = R * 0.299 + G * 0.587 + B * 0.114 + (1 - A) * 255;

    return brightness > 186 ? '#FFFFFF' : '#000000';
  }

  checkBackgroundColor() {
    if (!this.isMenuOpen && this.scrollWrapper) {
      const burger = this.toggleMenuButton.getBoundingClientRect();

      if (this.scrollWrapper.scrollTop < burger.bottom) {
        if (document.body.classList.contains('-menu-on-top')) {
          const section = this.pageBackground[0];
          const currentSection = section;

          const sectionStyle = window.getComputedStyle(currentSection);
          const currentBackgroundColor = this.getBackgroundColor(
            sectionStyle.backgroundColor,
          );

          if (currentBackgroundColor === '#000000') {
            this.toggleMenuButton.classList.remove('black');
            this.toggleMenuButton.classList.add('white');
            if (document.querySelector('.menu-bar .logo svg path')) {
              document.querySelector('.menu-bar .logo svg path').style.fill = '#fff';
            }
            if (document.body.classList.contains('powerfoyle')) {
              const logo = document.querySelector('.header-powerfoyle .logo svg path');
              if (logo) {
                logo.style.fill = '#fff';
              }
            }
          } else {
            this.toggleMenuButton.classList.remove('white');
            this.toggleMenuButton.classList.add('black');
          }
        } else {
          this.toggleMenuButton.classList.add('black');
          this.toggleMenuButton.classList.remove('white');
        }
        return;
      }
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.pageBackground.length; i++) {
        const section = this.pageBackground[i];
        const sect = section.getBoundingClientRect();

        if (
          sect.top < burger.bottom
          && sect.bottom > burger.bottom
        ) {
          const currentSection = section;

          const sectionStyle = window.getComputedStyle(currentSection);
          const currentBackgroundColor = this.getBackgroundColor(
            sectionStyle.backgroundColor,
          );

          if (currentBackgroundColor === '#000000') {
            this.toggleMenuButton.classList.remove('black');
            this.toggleMenuButton.classList.add('white');
          } else {
            this.toggleMenuButton.classList.remove('white');
            this.toggleMenuButton.classList.add('black');
          }
        }
      }
    }
  }

  setupLanguageSwitcher() {
    const languageSwitchers = [...document.querySelectorAll('.language-switcher')];

    languageSwitchers.forEach((languageSwitcher) => {
      if (!languageSwitcher) { return; }
      const switcherButton = languageSwitcher.querySelector('.switcher-button');
      const languages = languageSwitcher.querySelector('.languages');
      const languageButtons = languages.querySelectorAll('button');
      [...languageButtons].forEach((button) => {
        button.addEventListener('click', () => {
          window.location.href = button.dataset.url;
        });
      });

      const background = this.scrollWrapper.querySelector('.language-switcher-background');

      switcherButton.addEventListener('click', () => {
        languageSwitcher.classList.toggle('-open');
        background.classList.toggle('-visible');
      });

      background.addEventListener('click', () => {
        languageSwitcher.classList.toggle('-open');
        background.classList.toggle('-visible');
      });
    });
  }
}


domReady(() => {
  Menu.init();
});
